

import React, { useRef, useState } from 'react'
import { Input, Button, Radio, Spinner, Switcher, Tooltip, Badge, Select} from 'components/ui'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { HiOutlinePlusCircle, HiOutlineMinusCircle, HiOutlineTrash, HiOutlineExclamation } from 'react-icons/hi'
import { formatterInputSelectData } from 'utils/storeHube/helpers'
import { filterVisibleQuestions, guidGenerator, handleNameChange, handleOptionChange, onCheckRequired,  removeInvisibleOptions, setOptionIncident, setOptionPenality } from './helpers'
import { FiSave } from 'react-icons/fi'
import AnswerField from './AnswerField'
// Umbral compartido con el builder: es la misma decision de presentacion de los dos lados,
// y con una constante por lado se separaban. `constants.js` es un modulo de valores puros,
// sin imports, asi que traerlo aca no arrastra el builder a la pantalla de responder.
import { MAX_OPCIONES_A_LA_VISTA } from 'views/questionGroupsStudio/constants'

// `renderAfterOption` dibuja algo justo debajo de una opcion, dentro de la lista. Existe
// para la pregunta hija: colgada al final del bloque, con dos opciones parece colgar de la
// ultima que se lee, y Sabri lo pidio explicito el 2026-09-08 mirando la pantalla ("las
// opciones deberian estar debajo del SI, que es quien tiene las hijas"). Es opcional: sin
// el prop, la lista se pinta exactamente como antes.
const QuestionTypeRadio = ({ handleAddNewQuestion, questionTypeId, question, edit, handleRemoveQuestion, isAnswer, questionForAnswer, onChange, errorMessage, defaultValue, renderAfterOption }) => {

  const { t } = useTranslation()

  const [newOptionEdit, setNewOptionEdit] = useState(false)
  const [optionList, setOptionList] = useState(question ? formatterInputSelectData(question.question_option) : [])
  const [nameQuestion, setNameQuestion] = useState(question ? question.name : "")
  const [uniqueUuid, setUniqueUuid] = useState(question ? (question.uuid || question.uuid2) : "")
  const [optionListModified, setOptionListModified] = useState(question ? question.question_option : [])
  const [questionType_id, setQuestionTypeId] = useState(question ? question.question_type_id : questionTypeId)
  const [questionRequired, setQuestionRequired] = useState(question ? question.required : false)
  const [loading, setLoading] = useState(false)
  const [questionTag, setQuestionTag] = useState(question && question.tags ? question.tags[0] : null)
  const [points, setPoints] = useState(0)
  const [selectedPoints, setSelectedPoints] = useState(null);
  const [questionPoints, setQuestionPoints] = useState(question ? question.points : 0)
  const [isRemoving, setIsRemoving] = useState(false)
  // Branch `isAnswer`: controla que option-card esta seleccionada.
  const [selectedAnswer, setSelectedAnswer] = useState(defaultValue)

  const inputRef = useRef(null)

  useEffect(() => {
    if (newOptionEdit) {
      inputRef.current.focus()
    }
  }, [newOptionEdit])

  // FUNCIÓN ORIGINAL: Marca como invisible (tu lógica original)
  const removeOption = (option) => {
    const modifiedOption = { ...option, visible: false };
    const newOptionList = optionList.filter((item) => item.label !== option.label);
    newOptionList.push(modifiedOption);
    setOptionList(newOptionList);
    setOptionListModified(newOptionList);
  };

  const onNewOptionAdd = () => {
    const newUuid2 = guidGenerator();
    const newOption = {
      name: inputRef.current.value,
      label: inputRef.current.value,
      checked: false,
      visible: true,
      points: points,
      uuid2: newUuid2,
      incidents: false, // Inicializar explícitamente
      penality: false,  // Inicializar explícitamente
      priority: 0       // Inicializar explícitamente
    };
    
    const newOptionModified = {
      name: inputRef.current.value,
      uuid2: newUuid2,
      points: points,
      incidents: false,
      penality: false,
      priority: 0
    };
    
    setOptionList(prevOption => [...prevOption, newOption]);
    setOptionListModified(prevOption => [...prevOption, newOptionModified]);
    setNewOptionEdit(false);
    inputRef.current.value = "";
  }

  // FUNCIÓN CORREGIDA: Usar ID único en lugar de índice
  const onOptionCheckChange = (checked, optionId) => {
    setOptionList(prevOption => {
      const mutatedPrevOption = prevOption.map((option) => {
        const currentOptionId = option.uuid || option.uuid2;
        if (currentOptionId === optionId) {
          return { ...option, checked: checked };
        }
        return option;
      });
      return mutatedPrevOption;
    });
  }

  // FUNCIÓN CORREGIDA para manejar cambios en opciones
  const handleOptionChangeLocal = (e, id, field) => {
    let formattedValue = e.target.value;
    
    if (field === 'points') {
        formattedValue = formattedValue.replace(/[^0-9.]/g, ''); 
        const pointCount = (formattedValue.match(/\./g) || []).length;
        
        if (pointCount > 1) {
            formattedValue = formattedValue.replace(/\.+$/, '');
        }

        const decimalPart = formattedValue.split('.')[1];
        if (decimalPart && decimalPart.length > 2) {
            formattedValue = formattedValue.slice(0, formattedValue.indexOf('.') + 3);
        }
    } else if (field === 'priority') {
        formattedValue = formattedValue.replace(/[^0-9]/g, '');
    }

    if (formattedValue === '' || formattedValue === null) {
        formattedValue = '0';
    }

    const updateOptions = (prevOptions) => {
        return prevOptions.map((option) => {
            const optionId = option.uuid || option.uuid2;
            if (optionId === id) {
                return { ...option, [field]: formattedValue };
            }
            return option;
        });
    };

    setOptionList(updateOptions);
    setOptionListModified(updateOptions);
  };

  // FUNCIÓN CORREGIDA para manejar incidencias
  const setOptionIncidentLocal = (checked, optionId) => {
    const updateOptions = (prevOptions) => {
      return prevOptions.map((option) => {
        const currentOptionId = option.uuid || option.uuid2;
        if (currentOptionId === optionId) {
          return { ...option, incidents: checked };
        }
        return option;
      });
    };

    setOptionList(updateOptions);
    setOptionListModified(updateOptions);
  };

  // FUNCIÓN CORREGIDA para manejar penalizaciones
  const setOptionPenalityLocal = (checked, optionId) => {
    const updateOptions = (prevOptions) => {
      return prevOptions.map((option) => {
        const currentOptionId = option.uuid || option.uuid2;
        if (currentOptionId === optionId) {
          return { ...option, penality: checked };
        }
        return option;
      });
    };

    setOptionList(updateOptions);
    setOptionListModified(updateOptions);
  };

  const updateQuestions = () => {
    setLoading(true)
    handleAddNewQuestion(nameQuestion, optionList, questionType_id, questionRequired, uniqueUuid, undefined, undefined, questionPoints)
    setPoints(0)
    setSelectedPoints(null)
    setTimeout(() => {
      setLoading(false)
    }, 1000);
  }

  useEffect(() => {
    !isAnswer && updateQuestions()
  }, [optionListModified, nameQuestion, questionRequired, optionList, questionPoints])

  // Solo la rama `isAnswer` las usa. `question_option` no existe mientras se edita.
  const opcionesALaVista = isAnswer ? filterVisibleQuestions(questionForAnswer?.question_option || []) : []
  const opcionElegida = opcionesALaVista.find((o) => String(o.id) === String(selectedAnswer))
  const comoDesplegable = opcionesALaVista.length > MAX_OPCIONES_A_LA_VISTA

  // El envoltorio de la rama vive aca y no en quien la dibuja: el control es el unico que
  // sabe de que opcion cuelga y cuanto aire hace falta para que se lea colgando de ella y
  // no como la pregunta siguiente. El consumidor pasa solo el contenido.
  const ramaDe = (option) => {
    const contenido = renderAfterOption?.(option)
    if (!contenido) return null
    return (
      <div className="mt-3 mb-2 ml-1 pl-4 pb-1 border-l-2 border-indigo-200 dark:border-indigo-800/70 space-y-4">
        {contenido}
      </div>
    )
  }

  return (
    <>
      {
        isAnswer ?
          <AnswerField
            label={questionForAnswer?.name}
            required={questionForAnswer?.required}
            error={errorMessage}
          >
            {comoDesplegable ? (
              // De cinco opciones para arriba la lista abierta se come la pantalla, asi que
              // se responde con un desplegable. Es el mismo umbral y la misma constante que
              // usa el builder para plegar la pregunta mientras se la edita
              // (`MAX_OPCIONES_A_LA_VISTA`): con dos umbrales, la pregunta que se ve
              // desplegable al armarla aparecia como lista de ocho al responderla.
              //
              // La hija va debajo del control y no debajo de su opcion: entre dos `<option>`
              // de un desplegable no hay lugar en el DOM donde colgarla.
              <>
                <Select
                  options={opcionesALaVista.map((o) => ({ ...o, value: o.id, label: o.name }))}
                  value={opcionElegida ? { ...opcionElegida, value: opcionElegida.id, label: opcionElegida.name } : null}
                  isClearable
                  size="md"
                  placeholder={t('select_option')}
                  onChange={(op) => { setSelectedAnswer(op?.id); onChange(op?.id) }}
                />
                {opcionElegida && ramaDe(opcionElegida)}
              </>
            ) : (
            <div className="flex flex-col gap-2">
              {opcionesALaVista.map((option) => {
                const isSelected = String(selectedAnswer) === String(option.id)
                const debajo = ramaDe(option)
                // El contenido de `renderAfterOption` va FUERA del `<label>`: un campo de
                // texto dentro de la etiqueta de un radio hace que escribir en el marque
                // el radio.
                return (
                  <React.Fragment key={option.uuid || option.uuid2 || option.id}>
                    <label
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${isSelected
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                        }`}
                    >
                      <input
                        type="radio"
                        name={questionForAnswer.id}
                        className="text-indigo-600 focus:ring-indigo-500"
                        checked={isSelected}
                        onChange={() => { setSelectedAnswer(option.id); onChange(option.id) }}
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-200">{option.name}</span>
                    </label>
                    {debajo}
                  </React.Fragment>
                )
              })}
            </div>
            )}
            {/* En el desplegable no va: el control ya trae su propia cruz para limpiar, y
                el boton quedaba debajo de la pregunta hija, leyendose como parte de ella. */}
            {!comoDesplegable && selectedAnswer !== undefined && selectedAnswer !== null && selectedAnswer !== '' && (
              <button
                type="button"
                onClick={() => { setSelectedAnswer(undefined); onChange(undefined) }}
                className="mt-2 text-xs text-gray-400 hover:text-rose-500 transition-colors"
              >
                {t('questionnaire_clear_answer')}
              </button>
            )}
          </AnswerField>
          :
          <>
            <Input onChange={(event) => handleNameChange(setNameQuestion, event)} placeholder={t("question")} value={nameQuestion} />
            {edit && questionTag &&
              <div className=" flex items-center mr-2 rtl:ml-2 mt-3 gap-2">
                <p className=' capitalize '>{t("tag")} : </p>
                <div className="flex items-center gap-2 border rounded-lg px-1">
                  <Badge style={{ "backgroundColor": `${questionTag.color}` }} />
                  <span className={`capitalize font-semibold `}>
                    {questionTag.name}
                  </span>
                </div>
              </div>
            }
            {
              optionList.length > 0 &&
              <div className="flex flex-col">
                <div className='mt-4'>
                  {removeInvisibleOptions(optionList).map((option) => {
                    const optionId = option.uuid || option.uuid2;
                    return (
                      <div key={optionId} className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-between mb-5 ">
                        <div className="flex flex-row items-center justify-between">
                          <div className='mr-5' onClick={() => removeOption(option)}>
                            <HiOutlineMinusCircle className=' text-lg ' />
                          </div>
                          
                          <Radio
                            key={`radio-edit-${optionId}`} // CLAVE ÚNICA PARA RADIO
                            defaultChecked={option.checked}
                            className="flex flex-row"
                            onChange={checked => onOptionCheckChange(checked, optionId)} // USAR ID EN LUGAR DE ÍNDICE
                          >
                            {option.label}
                          </Radio>
                          
                          <div className=' ml-2 h-6 flex items-center gap-2 text-center'>
                            Puntos:
                            <input
                              type="text"
                              className="input h-6 w-[60px] border border-gray-300 p-2 rounded text-center"
                              onChange={(e) => handleOptionChangeLocal(e, optionId, 'points')}
                              value={option.points || 0}
                            />

                            Orden:
                            <input
                              type="text"
                              className="input h-6 w-[40px] border border-gray-300 p-2 rounded text-center"
                              onChange={(e) => handleOptionChangeLocal(e, optionId, 'priority')}
                              value={option.priority || 0}
                              placeholder="Priority"
                            />
                          </div>

                          <div className='ml-5 gap-2  mt-2 '>
                            <Tooltip title={t("message_incidence")}>
                              {option.incidents && <HiOutlineExclamation className=' text-lg ' />}
                            </Tooltip>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-3 items-end" >
                          <div className='flex items-center mr-2'>
                            <span className='mr-2'> {t("check_incidence")}</span>
                            <Switcher 
                              key={`incidents-${optionId}`} // CLAVE ÚNICA
                              defaultChecked={option.incidents} 
                              onChange={(checked) => setOptionIncidentLocal(checked, optionId)} 
                            />
                          </div>
                          {
                            option.incidents &&
                            <div className='flex items-center mr-2 '>
                              <span className='mr-2'> {t("check_penality")}</span>
                              <Switcher 
                                key={`penality-${optionId}`} // CLAVE ÚNICA
                                defaultChecked={option.penality} 
                                onChange={(checked) => setOptionPenalityLocal(checked, optionId)} 
                              />
                            </div>
                          }
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            }
            <>
              <div className="flex items-center gap-2">
                <Input ref={inputRef} placeholder={t("question_options")} />
              </div>
              
              <div className="flex items-center gap-2 my-3">
                <Button type="button" onClick={onNewOptionAdd} icon={<HiOutlinePlusCircle />}></Button>
              </div>

              <div className='flex items-center mb-3 justify-end'>
                <div className='flex items-center mr-2'>
                  <span className='mr-2'>{t("question_points")}:</span>
                  <input
                    type="text"
                    className="input h-8 w-[80px] border border-gray-300 p-2 rounded text-center"
                    value={questionPoints}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setQuestionPoints(value === '' ? 0 : value);
                    }}
                    placeholder="0"
                  />
                </div>
                <div className='flex items-center mr-2'>
                  <span className='mr-2'> {t("isRequired")}</span>
                  <Switcher defaultChecked={question && question.required} onChange={(event) => onCheckRequired(setQuestionRequired, event)} />
                </div>
                {
                  edit &&
                  <>
                    <div
                      className={`mr-5 cursor-pointer transition-colors duration-200 ${isRemoving ? 'text-gray-400 pointer-events-none' : 'hover:text-red-500'}`}
                      onClick={() => {
                        setIsRemoving(true)
                        setTimeout(() => handleRemoveQuestion(question), 600)
                      }}
                    >
                      <Tooltip title={isRemoving ? "Desactivando pregunta..." : "Desactivar pregunta"}>
                        {isRemoving ? <Spinner size={20} /> : <HiOutlineTrash className=' text-lg ' />}
                      </Tooltip>
                    </div>
                    <div className='mr-5' onClick={() => updateQuestions()}>
                      <Tooltip title="Guardar cambios">
                        {loading ? <Spinner /> : <FiSave className=' text-lg ' />}
                      </Tooltip>
                    </div>
                  </>
                }
              </div>
              {
                !edit && <Button
                  block
                  className="border-dashed mb-3"
                  onClick={() => handleAddNewQuestion(nameQuestion, optionListModified, questionType_id, questionRequired, uniqueUuid, undefined, undefined, questionPoints)}
                >
                  {t("save_question")}
                </Button>
              }
            </>
          </>
      }
    </>
  )
}

export default QuestionTypeRadio